import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Row,
  Form,
  Spinner,
  Offcanvas
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import * as Moment from 'moment';
import { usePagination, useSortBy, useTable } from 'react-table';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import api from '@services/api'; // Assurez-vous que le chemin est correct

const IndexPartnerSalons = () => {
  const [trigger, setTrigger] = useState(Date.now());
  const [initialApiObjects, setInitialApiObjects] = useState([]); // Conservera les données brutes de l'API
  const [displayObjects, setDisplayObjects] = useState([]); // Données transformées pour la table
  const [isLoading, setIsLoading] = useState(true);
  const [selectedObject, setSelectedObject] = useState({});
  const [showDelete, setShowDelete] = useState(false);

  const formatDate = dateString => {
    return Moment(dateString).format('DD/MM/YYYY');
  };

  const transformObjects = rawObjects => {
    if (!rawObjects) return [];
    return rawObjects.map(obj => ({
      // Garder l'id original pour la suppression et la modification
      id: obj.id,
      originalName: obj.name, // Garder le nom original pour l'offcanvas de suppression
      name: (
        <Link
          to={`/administration/salons-partenaires/${obj.id}`}
          className="fw-bold text-decoration-none"
        >
          {obj.name}
        </Link>
      ),
      date: formatDate(obj.date),
      schedule: `${obj.start_time} - ${obj.end_time}`,
      slots: `${obj.slot_duration} min`,
      actions: (
        <Col className="px-0">
          <Button
            size="sm"
            variant="primary"
            className="me-2 admin-primary"
            type="button"
            as={Link} // Utiliser as={Link} pour la navigation
            to={`/administration/salons-partenaires/${obj.id}/modifier`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="14"
              height="14"
              style={{
                marginBottom: '2px',
                marginRight: '3px'
              }}
            >
              <path
                d="M16.757 3l-2 2H5v14h14V9.243l2-2V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h12.757zm3.728-.9L21.9 3.516l-9.192 9.192-1.412.003-.002-1.417L20.485 2.1z"
                fill="rgba(255,255,255,1)"
              />
            </svg>
          </Button>
          <Button
            size="sm"
            variant="danger"
            className="me-0"
            type="button"
            onClick={() => {
              setSelectedObject(obj || {});
              setShowDelete(true);
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="14"
              height="14"
              style={{
                marginBottom: '2px'
              }}
            >
              <path
                d="M7 4V2h10v2h5v2h-2v15a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6H2V4h5zM6 6v14h12V6H6zm3 3h2v8H9V9zm4 0h2v8h-2V9z"
                fill="rgba(255,255,255,1)"
              />
            </svg>
          </Button>
        </Col>
      )
    }));
  };

  const deleteObject = async () => {
    console.log('Deleting salon:', selectedObject);
    if (!selectedObject.id) return;
    try {
      const response = await api.delete(
        `/admin/partner-salons/${selectedObject.id}`
      );
      if (response.data.success === true) {
        setShowDelete(false);
        toast('Salon partenaire supprimé');
        setTrigger(Date.now());
      } else {
        toast(
          response.data.message ||
            'Une erreur est survenue, veuillez réessayer plus tard'
        );
      }
    } catch (error) {
      console.error('Error deleting salon:', error);
      toast(
        error.response?.data?.message ||
          'Une erreur réseau est survenue lors de la suppression.'
      );
    }
  };

  useEffect(() => {
    const fetchObjects = async () => {
      setIsLoading(true);
      try {
        const response = await api.get('/admin/partner-salons');
        if (response.data.success === true) {
          setInitialApiObjects(response.data.data);
          setDisplayObjects(transformObjects(response.data.data));
        } else {
          toast(
            response.data.message ||
              'Une erreur est survenue lors de la récupération des salons.'
          );
        }
      } catch (error) {
        console.error('Error fetching salons:', error);
        toast(
          error.response?.data?.message ||
            'Une erreur réseau est survenue lors de la récupération des salons.'
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchObjects();
  }, [trigger]);

  return (
    <>
      <Offcanvas
        show={showDelete}
        onHide={() => {
          setShowDelete(false);
        }}
        placement={'end'}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            {selectedObject.id !== undefined && (
              <>
                La suppression du salon "{selectedObject.name}" est définitive.
              </>
            )}
            <br />
            Voulez-vous continuer ?
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Button
            size="sm"
            variant="falcon-danger"
            className="me-2"
            onClick={deleteObject}
          >
            Oui, supprimer
          </Button>
          <Button
            size="sm"
            variant="falcon-default"
            className="me-2"
            onClick={() => {
              setShowDelete(false);
            }}
          >
            Annuler
          </Button>
        </Offcanvas.Body>
      </Offcanvas>
      <Card>
        <Card.Body className="overflow-hidden px-lg-3">
          <Row className="justify-content-between align-items-center">
            <Col lg={6}>
              <h3 className="text-primary admin-title mb-0">
                Gestion des salons partenaires
              </h3>
            </Col>
            <Col xs={6} className="text-end">
              <Button
                variant="primary"
                className="admin-primary"
                size="sm"
                as={Link}
                to="/administration/salons-partenaires/ajouter"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  style={{ marginBottom: '2px', marginRight: '2px' }}
                >
                  <path
                    d="M11 11V5h2v6h6v2h-6v6h-2v-6H5v-2z"
                    fill="rgba(255,255,255,1)"
                  />
                </svg>
                Ajouter un salon
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      {isLoading === true && (
        <Row>
          <Col xs={12} className="mt-3 text-center">
            <Spinner animation="border" role="status" size="sm">
              <span className="visually-hidden">Chargement...</span>
            </Spinner>
          </Col>
        </Row>
      )}
      <Card className="mt-3">
        <Card.Body className="">
          <Row>
            {!isLoading && displayObjects && displayObjects.length > 0 ? (
              <Col xs={12} className="mt-0">
                {' '}
                {/* mt-3 enlevé pour coller au design précédent */}
                <Table data={displayObjects} />
              </Col>
            ) : (
              !isLoading && (
                <Col xs={12} className="mt-3 text-center">
                  <div className="p-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      width="48"
                      height="48"
                      className="mb-3 text-muted"
                    >
                      <path fill="none" d="M0 0h24v24H0z" />
                      <path
                        d="M20 22H4a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1zm-1-2V4H5v16h14zM8 7h8v2H8V7zm0 4h8v2H8v-2zm0 4h5v2H8v-2z"
                        fill="currentColor"
                      />
                    </svg>
                    <h5>Aucun salon partenaire disponible</h5>
                    <p className="text-muted mb-4">
                      Vous n'avez pas encore ajouté de salon partenaire. Cliquez
                      sur le bouton "Ajouter un salon" pour commencer.
                    </p>
                    <Button
                      variant="primary"
                      className="admin-primary"
                      as={Link}
                      to="/administration/salons-partenaires/ajouter"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        width="18"
                        height="18"
                        style={{ marginBottom: '2px', marginRight: '2px' }}
                      >
                        <path
                          d="M11 11V5h2v6h6v2h-6v6h-2v-6H5v-2z"
                          fill="rgba(255,255,255,1)"
                        />
                      </svg>
                      Ajouter un salon
                    </Button>
                  </div>
                </Col>
              )
            )}
          </Row>
        </Card.Body>
      </Card>
    </>
  );
};
// ... Le reste du composant Table reste inchangé ...
// ... (Table component remains the same)
function Table({ data }) {
  const columns = React.useMemo(
    () => [
      {
        accessor: 'name',
        Header: 'Nom'
      },
      {
        accessor: 'date',
        Header: 'Date'
      },
      {
        accessor: 'schedule',
        Header: 'Horaires'
      },
      {
        accessor: 'slots',
        Header: 'Durée des créneaux'
      },
      {
        accessor: 'actions',
        Header: 'Actions'
      }
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize }
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 15 }
    },
    useSortBy,
    usePagination
  );

  return (
    <>
      <div className="table-responsive scrollbar-visible table-container">
        <table
          {...getTableProps()}
          className="table table-striped table-bordered admin-table w-100 d-block d-table"
        >
          <thead>
            {headerGroups.map((headerGroup, index) => (
              <tr
                className="table-fixed-header"
                key={index}
                {...headerGroup.getHeaderGroupProps()}
              >
                {headerGroup.headers.map(column => (
                  <th
                    key={`${index}${Date.now()}${column.id}`} // Clé plus unique
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                  >
                    {column.render('Header')}
                    <span>
                      {column.isSorted ? (
                        column.isSortedDesc ? (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            width="18"
                            height="18"
                          >
                            <path fill="none" d="M0 0h24v24H0z" />
                            <path d="M12 13.172l4.95-4.95 1.414 1.414L12 16 5.636 9.636 7.05 8.222z" />
                          </svg>
                        ) : (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            width="18"
                            height="18"
                          >
                            <path fill="none" d="M0 0h24v24H0z" />
                            <path d="M12 10.828l-4.95 4.95-1.414-1.414L12 8l6.364 6.364-1.414 1.414z" />
                          </svg>
                        )
                      ) : (
                        ''
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row, i) => {
              prepareRow(row);
              return (
                <tr key={row.id || i} {...row.getRowProps()}>
                  {row.cells.map((cell, indexCell) => {
                    return (
                      <td key={indexCell} {...cell.getCellProps()}>
                        {cell.render('Cell')}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="pagination d-block mt-3">
        <Button
          variant="falcon-primary"
          size="sm"
          className="me-2"
          onClick={() => {
            gotoPage(0);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          disabled={!canPreviousPage}
        >
          {'<<'}
        </Button>
        <Button
          variant="falcon-primary"
          size="sm"
          className="me-2"
          onClick={() => {
            previousPage();
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          disabled={!canPreviousPage}
        >
          {'<'}
        </Button>
        <Button
          variant="falcon-primary"
          size="sm"
          className="me-2"
          onClick={() => {
            nextPage();
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          disabled={!canNextPage}
        >
          {'>'}
        </Button>
        <Button
          variant="falcon-primary"
          size="sm"
          className="me-3"
          onClick={() => {
            gotoPage(pageCount - 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          disabled={!canNextPage}
        >
          {'>>'}
        </Button>{' '}
        <span className="bottom-table">
          Page{' '}
          <strong>
            {pageIndex + 1} sur {pageOptions.length}
          </strong>{' '}
        </span>
        <Form.Select
          className="d-inline-block w-auto ms-3 table-select"
          value={pageSize}
          onChange={e => {
            setPageSize(Number(e.target.value));
          }}
          aria-label="Default select example"
        >
          {[15, 30, 50, 100, 150].map(
            (
              pageSizeVal // Renommé pageSize en pageSizeVal pour éviter conflit
            ) => (
              <option key={pageSizeVal} value={pageSizeVal}>
                Afficher {pageSizeVal} éléments
              </option>
            )
          )}
        </Form.Select>
      </div>
    </>
  );
}

Table.propTypes = {
  data: PropTypes.array
};

export default IndexPartnerSalons;
